import { parseCsv } from "../../utils/csv-parser.js";
import { openCsvFilePicker, readCsvFile } from "../../utils/csv-file-picker.js";
import { Alerts } from "../../utils/Alerts.js";
import { DataPreviewCell } from "./components/DataPreviewCell.js";
import { useEffect, useState } from "react";
import { Button, DinnerTable, Select } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import { useUpdateEffect } from "ahooks";
//#region src/components/CsvImportMapper/index.tsx
function CsvImportMapper({ allowInlineEditing, columnMapping, onComplete, onStepChange, setDocumentKey }) {
	const [step, setStep] = useState("pick-file");
	const [parsedCsv, setParsedCsv] = useState();
	const [mappedCsvData, setMappedCsvData] = useState([]);
	const [rowColValidationError, setRowColValidationError] = useState({});
	const [columnMappingPropKeyToCsvColumnIndexes, setColumnMappingPropKeyToCsvColumnIndexes] = useState({});
	const [isColumnMappingComplete, setIsColumnMappingComplete] = useState(false);
	const [isMappingInvalid, setIsMappingInvalid] = useState(true);
	useUpdateEffect(() => {
		if (onStepChange) onStepChange(step);
	}, [step]);
	useEffect(() => {
		if (!parsedCsv) return;
		const allRequiredMapped = columnMapping.filter((col) => col.required).every((col) => columnMappingPropKeyToCsvColumnIndexes[col.propKey] !== void 0);
		setIsColumnMappingComplete(allRequiredMapped);
	}, [columnMappingPropKeyToCsvColumnIndexes]);
	useEffect(() => {
		if (step === "finalize" && !mappedCsvData.length) {
			const mappedData = parsedCsv?.rows.map((row) => {
				const mappedRow = {};
				columnMapping.forEach((column) => {
					const csvIndex = columnMappingPropKeyToCsvColumnIndexes[column.propKey];
					if (csvIndex === void 0) mappedRow[column.propKey] = void 0;
					else if (Array.isArray(csvIndex)) {
						const mappedCsvIndex = csvIndex.map((i) => row[i] !== void 0 ? row[i] : void 0);
						mappedRow[column.propKey] = mappedCsvIndex.map((value) => value).join(", \n");
					} else mappedRow[column.propKey] = row[csvIndex] !== void 0 ? row[csvIndex] : void 0;
				});
				return mappedRow;
			});
			setMappedCsvData(mappedData ?? []);
		}
	}, [step]);
	const checkForValidValues = (mappedValues = mappedCsvData) => {
		const mappedColumns = columnMapping.filter((column) => columnMappingPropKeyToCsvColumnIndexes[column.propKey] !== void 0);
		if (!mappedColumns.length || !mappedValues.length) return true;
		if (mappedColumns.length) {
			if (!mappedValues.some((row) => {
				return mappedColumns.some((column) => {
					const value = row[column.propKey] ?? "";
					return value !== "" && value !== void 0;
				});
			})) {
				setMappedCsvData([]);
				setIsMappingInvalid(true);
				return false;
			}
		}
		const isColumnValueValid = (value, column) => {
			if (column.valueType === void 0) return true;
			if (column.valueType === "number") {
				if (value === void 0) return true;
				if (!Number.isFinite(Number(value))) setRowColValidationError((prev) => ({
					...prev,
					[column.label]: "Number Required"
				}));
				return Number.isFinite(Number(value));
			}
			return true;
		};
		const valid = [];
		mappedColumns.map((column) => {
			mappedValues.some((row) => {
				const value = row[column.propKey] ?? "";
				valid.push(isColumnValueValid(value, column));
			});
		});
		setIsMappingInvalid(valid.includes(false));
	};
	useEffect(() => {
		const validationCbsByPropKey = columnMapping.reduce((acc, column) => {
			if (column.validation) acc[column.propKey] = column.validation;
			return acc;
		}, {});
		const errors = mappedCsvData.reduce((acc, row, rowIndex) => {
			Object.entries(row).forEach(([propKey, value], colIndex) => {
				const error = validationCbsByPropKey[propKey]?.(value);
				if (error) acc[`${rowIndex}-${colIndex}`] = error.error;
			});
			return acc;
		}, {});
		setRowColValidationError(errors);
		checkForValidValues(mappedCsvData);
	}, [mappedCsvData]);
	const handleDoneClick = () => {
		onComplete(mappedCsvData);
		onStepChange?.("info");
	};
	const handlePickCsvFileClick = () => {
		openCsvFilePicker(async (file) => {
			try {
				setDocumentKey?.(file.name);
				const parsed = await parseCsv(await readCsvFile(file));
				setParsedCsv(parsed);
				const initialMapping = {};
				parsed.columns.forEach((colName, index) => {
					const matchingColumn = columnMapping.find((col) => col.label.replace(/[^a-zA-Z]/g, "").toLowerCase() === colName.replace(/[^a-zA-Z]/g, "").toLowerCase());
					if (matchingColumn) initialMapping[matchingColumn.propKey] = index;
				});
				setColumnMappingPropKeyToCsvColumnIndexes(initialMapping);
				setStep("select-mapping");
			} catch (e) {
				Alerts.error(e.message || "Failed to open/parse CSV file");
			}
		}, { multiple: false });
	};
	const updateMappedCsvDataColumnData = (propKey, csvColumnIndex) => {
		setMappedCsvData((prev) => {
			return prev.map((row, index) => {
				const newRow = { ...row };
				if (csvColumnIndex === void 0) newRow[propKey] = void 0;
				else if (Array.isArray(csvColumnIndex)) newRow[propKey] = csvColumnIndex.map((i) => parsedCsv?.rows[index]?.[i] !== void 0 ? parsedCsv?.rows[index]?.[i] : void 0).map((value) => value).join(", \n");
				else newRow[propKey] = parsedCsv?.rows[index]?.[csvColumnIndex] || void 0;
				return newRow;
			});
		});
	};
	const noMappedColumns = mappedCsvData.length ? mappedCsvData.every((data) => !Object.values(data).some((value) => value !== null && value !== void 0 && value !== "")) : true;
	if (step === "pick-file") return /* @__PURE__ */ jsx("div", {
		className: "flex w-full justify-center",
		children: /* @__PURE__ */ jsx("div", {
			className: "flex w-1/2 justify-center",
			children: /* @__PURE__ */ jsx(Button, {
				onClick: handlePickCsvFileClick,
				variant: "main",
				children: "Upload CSV"
			})
		})
	});
	if (step === "select-mapping") return /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsxs("div", {
		className: "py-base flex items-center justify-between",
		children: [/* @__PURE__ */ jsx("div", {
			className: "text-md",
			children: "Column Mapping"
		}), /* @__PURE__ */ jsx(Button, {
			disabled: !isColumnMappingComplete,
			onClick: () => setStep("finalize"),
			variant: "main",
			children: "Next"
		})]
	}), /* @__PURE__ */ jsxs(DinnerTable, { children: [/* @__PURE__ */ jsx(DinnerTable.Header, { children: /* @__PURE__ */ jsxs(DinnerTable.Row, { children: [/* @__PURE__ */ jsx(DinnerTable.Cell, { children: "Column Name" }), /* @__PURE__ */ jsx(DinnerTable.Cell, { children: "CSV Column" })] }) }), /* @__PURE__ */ jsx(DinnerTable.Body, { children: columnMapping.map((column) => {
		const selectedCsvColumnIndex = columnMappingPropKeyToCsvColumnIndexes[column.propKey];
		const csvColumnsOptions = parsedCsv?.columns.map((csvColName, csvColumnIndex) => {
			return {
				label: csvColName,
				value: csvColumnIndex
			};
		}) ?? [];
		return /* @__PURE__ */ jsxs(DinnerTable.Row, { children: [/* @__PURE__ */ jsx(DinnerTable.Cell, { children: /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("div", { children: column.label }), /* @__PURE__ */ jsx("div", {
			className: "text-neutral text-sm",
			children: column.subText
		})] }) }), /* @__PURE__ */ jsx(DinnerTable.Cell, { children: /* @__PURE__ */ jsx(Select, {
			isClearable: !column.required && column.allowMultiValue !== true,
			combineSelected: column.allowMultiValue,
			multi: column.allowMultiValue,
			options: csvColumnsOptions,
			onChangeValue: (parsedCsvColumnIndex) => {
				updateMappedCsvDataColumnData(column.propKey, parsedCsvColumnIndex);
				if (parsedCsvColumnIndex === void 0) setColumnMappingPropKeyToCsvColumnIndexes((prev) => {
					const newObj = { ...prev };
					delete newObj[column.propKey];
					return newObj;
				});
				else setColumnMappingPropKeyToCsvColumnIndexes((prev) => ({
					...prev,
					[column.propKey]: parsedCsvColumnIndex
				}));
			},
			value: selectedCsvColumnIndex
		}) })] }, column.propKey);
	}) })] })] });
	if (step === "finalize") return /* @__PURE__ */ jsxs("div", { children: [
		/* @__PURE__ */ jsxs("div", {
			className: "py-base flex items-center justify-between",
			children: [/* @__PURE__ */ jsx("div", {
				className: "text-md",
				children: "Data Preview"
			}), /* @__PURE__ */ jsxs("div", {
				className: "gap-sm flex items-center justify-between",
				children: [/* @__PURE__ */ jsx(Button, {
					onClick: () => setStep("select-mapping"),
					variant: "main",
					outline: true,
					children: "Edit Column Mapping"
				}), /* @__PURE__ */ jsx(Button, {
					disabled: !isColumnMappingComplete || !Object.values(mappedCsvData).length || isMappingInvalid,
					onClick: handleDoneClick,
					variant: "main",
					children: "Done"
				})]
			})]
		}),
		(!mappedCsvData.length || noMappedColumns) && /* @__PURE__ */ jsxs("div", {
			className: "p-base text-caution border-caution rounded border text-center",
			children: ["No data mapped for preview. Please ensure you have mapped the columns correctly.", /* @__PURE__ */ jsx("div", {
				className: "pt-base",
				children: /* @__PURE__ */ jsx(Button, {
					onClick: () => setStep("select-mapping"),
					variant: "caution",
					children: "Edit Column Mapping"
				})
			})]
		}),
		!noMappedColumns && isMappingInvalid && /* @__PURE__ */ jsxs("div", {
			className: "p-base border-caution rounded border text-center",
			children: [
				/* @__PURE__ */ jsx("div", {
					className: "text-caution",
					children: "Data mapped incorrectly. Please ensure you have mapped the columns correctly."
				}),
				/* @__PURE__ */ jsxs(DinnerTable, { children: [/* @__PURE__ */ jsx(DinnerTable.Header, { children: /* @__PURE__ */ jsxs(DinnerTable.Row, { children: [/* @__PURE__ */ jsx(DinnerTable.Cell, { children: "Column Name" }), /* @__PURE__ */ jsx(DinnerTable.Cell, { children: "Reason" })] }) }), /* @__PURE__ */ jsx(DinnerTable.Body, { children: rowColValidationError && Object.keys(rowColValidationError).map((column) => {
					return /* @__PURE__ */ jsxs(DinnerTable.Row, { children: [/* @__PURE__ */ jsx(DinnerTable.Cell, { children: /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx("div", { children: column }) }) }), /* @__PURE__ */ jsx(DinnerTable.Cell, { children: rowColValidationError[column] })] }, column);
				}) })] }),
				/* @__PURE__ */ jsx("div", {
					className: "pt-base",
					children: /* @__PURE__ */ jsx(Button, {
						onClick: () => setStep("select-mapping"),
						variant: "caution",
						children: "Edit Column Mapping"
					})
				})
			]
		}),
		!!mappedCsvData.length && !isMappingInvalid && /* @__PURE__ */ jsxs(DinnerTable, { children: [/* @__PURE__ */ jsx(DinnerTable.Header, { children: /* @__PURE__ */ jsx(DinnerTable.Row, { children: columnMapping.map((column, columnMappingIndex) => {
			return /* @__PURE__ */ jsx(DinnerTable.Cell, { children: column.label }, column.propKey);
		}) }) }), /* @__PURE__ */ jsx(DinnerTable.Body, { children: mappedCsvData.map((row, rowIndex) => /* @__PURE__ */ jsx(DinnerTable.Row, { children: Object.entries(row).map(([key, value], colIndex) => /* @__PURE__ */ jsx(DinnerTable.Cell, { children: /* @__PURE__ */ jsx(DataPreviewCell, {
			allowInlineEditing,
			columnMapping: columnMapping[colIndex],
			onChangeValue: (newValue) => {
				const error = columnMapping[colIndex]?.validation?.(newValue);
				if (error) setRowColValidationError((prev) => ({
					...prev,
					[`${rowIndex}-${colIndex}`]: error.error
				}));
				else setRowColValidationError((prev) => {
					const newErrors = { ...prev };
					delete newErrors[`${rowIndex}-${colIndex}`];
					return newErrors;
				});
				setMappedCsvData((prev) => {
					const newData = [...prev];
					newData[rowIndex][key] = newValue;
					return newData;
				});
			},
			validationError: rowColValidationError[`${rowIndex}-${colIndex}`],
			value
		}, `${rowIndex}-${key}`) }, key)) }, rowIndex)) })] })
	] });
}
//#endregion
export { CsvImportMapper };

//# sourceMappingURL=index.js.map